import { HTMLTable, Label } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';

import { TEST_IDS } from 'src/constants/testIds';
import { ProjectDiagramAttributesTableAddRow } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTableAddRow';
import { ProjectDiagramAttributesTableEditRow } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTableEditRow';
import { ProjectDiagramAttributesTableRow } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTableRow';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { useUpdateAttribute } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateAttribute';
import * as copy from 'src/templates/copy';

type ProjectDiagramAttributesTableProps = {
  attributes: Array<AttributeTableItem & { depth?: number }>;
  onAddAttribute: (item: AttributeTableItem) => void;
  onClickLink?: (item: AttributeTableItem) => void;
  onUnlinkAttribute: (item: AttributeTableItem) => void;
  onUpdateAttribute?: ReturnType<typeof useUpdateAttribute>;
  additionalIris?: Set<string>;
};

export const ProjectDiagramAttributesTable: React.VFC<
  ProjectDiagramAttributesTableProps
> = ({
  attributes,
  onAddAttribute,
  onClickLink,
  onUnlinkAttribute,
  onUpdateAttribute,
  additionalIris,
}) => {
  const [editId, setEditId] = React.useState<string | null>(null);

  const attributeIris = React.useMemo(
    () => new Set(attributes.map((item) => item.value)),
    [attributes]
  );
  const { highlightedIndices, sortedAttributes } = React.useMemo(() => {
    const sortedAttributes = attributes.sort(alphaSort(({ text }) => text));
    const highlightedIndices = new Set<number>();
    sortedAttributes.forEach((attribute, idx) => {
      if (attribute.highlighted) {
        highlightedIndices.add(idx);
      }
    });
    return { highlightedIndices, sortedAttributes };
  }, [attributes]);

  return (
    <div>
      <Label className="sd-drawer-project-diagram-entity-table-label">
        {copy.components.projectDiagram.attributesTable.label}
      </Label>
      <HTMLTable
        className={classNames(
          'sd-drawer-project-diagram-entity-table',
          'sd-drawer-project-diagram-entity-table--attributes'
        )}
        data-testid={TEST_IDS.projectDiagram.entityDrawer.attributesTable}
      >
        <thead>
          <tr>
            <th>{copy.components.projectDiagram.attributesTable.nameColumn}</th>
            <th>{copy.components.projectDiagram.attributesTable.typeColumn}</th>
            <th className="sd-drawer-project-diagram-entity-table--attributes-rules-header">
              {copy.components.projectDiagram.attributesTable.rule}
            </th>
            <th>{}</th>
          </tr>
        </thead>

        <tbody>
          <ProjectDiagramAttributesTableAddRow
            additionalIris={additionalIris}
            currentAttributeIris={attributeIris}
            onAddAttribute={onAddAttribute}
          />
          {sortedAttributes.map((attribute, idx) => {
            return editId === attribute.value ? (
              <ProjectDiagramAttributesTableEditRow
                key={attribute.value}
                attribute={attribute}
                idx={idx}
                onSubmit={(update) => {
                  if (update) {
                    onUpdateAttribute?.(update);
                  }
                  setEditId(null);
                }}
              />
            ) : (
              <ProjectDiagramAttributesTableRow
                key={attribute.value}
                attribute={attribute}
                idx={idx}
                isPreviousRowHighlighted={highlightedIndices.has(idx - 1)}
                onClickLink={onClickLink}
                onStartEdit={
                  onUpdateAttribute
                    ? () => setEditId(attribute.value)
                    : undefined
                }
                onUnlink={onUnlinkAttribute}
              />
            );
          })}
        </tbody>
      </HTMLTable>
    </div>
  );
};
